<div class="min-h-screen bg-gray-50 flex flex-col">
  <!-- Hero Section -->
  <section class="bg-gradient-to-r from-immovea-navy via-blue-800 to-blue-900 text-white py-12 sm:py-20 lg:py-24">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h1 class="text-4xl font-bold">Welcome Back</h1>
      <p class="text-blue-100 mt-2">Sign in to manage your properties and messages</p>
    </div>
  </section>

  <!-- Login Form Section -->
  <div class="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
    <div class="w-full max-w-md">
      <!-- Logo & Title -->
      <div class="text-center mb-8">
        <h2 class="text-3xl font-bold text-gray-900 mb-2">Sign In</h2>
        <p class="text-gray-600">Enter your credentials to continue</p>
      </div>

      <!-- Login Form -->
      <form (ngSubmit)="onSubmit()" class="bg-white rounded-xl shadow-lg p-8 space-y-6">
        <!-- Error Message -->
        <div *ngIf="errorMessage()" class="rounded-lg bg-red-50 border border-red-200 p-4">
          <div class="flex items-center gap-3">
            <svg class="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
            <p class="text-sm text-red-800">{{ errorMessage() }}</p>
          </div>
        </div>

        <!-- Email Input -->
        <div>
          <label for="email" class="block text-sm font-semibold text-gray-900 mb-2">Email address</label>
          <div class="relative">
            <svg class="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
            <input 
              id="email" 
              type="email" 
              [(ngModel)]="email" 
              name="email" 
              required 
              autocomplete="email" 
              placeholder="admin@example.com"
              [class.border-red-500]="emailError()"
              class="block w-full pl-10 pr-4 py-2.5 border rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-immovea-navy focus:border-transparent transition"
            />
          </div>
          <p *ngIf="emailError()" class="mt-1.5 text-sm text-red-600 flex items-center gap-1">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
            </svg>
            {{ emailError() }}
          </p>
        </div>

        <!-- Password Input -->
        <div>
          <label for="password" class="block text-sm font-semibold text-gray-900 mb-2">Password</label>
          <div class="relative">
            <svg class="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
            </svg>
            <input 
              id="password" 
              type="password" 
              [(ngModel)]="password" 
              name="password" 
              required 
              autocomplete="current-password" 
              placeholder="••••••••"
              [class.border-red-500]="passwordError()"
              class="block w-full pl-10 pr-4 py-2.5 border rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-immovea-navy focus:border-transparent transition"
            />
          </div>
          <p *ngIf="passwordError()" class="mt-1.5 text-sm text-red-600 flex items-center gap-1">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
            </svg>
            {{ passwordError() }}
          </p>
        </div>

        <!-- Sign In Button -->
        <button 
          type="submit" 
          [disabled]="loading()"
          class="w-full bg-gradient-to-r from-immovea-navy to-blue-900 text-white py-2.5 rounded-lg hover:shadow-lg transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <svg *ngIf="!loading()" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
          </svg>
          {{ loading() ? 'Signing in...' : 'Sign In' }}
        </button>
      </form>
    </div>
  </div>
</div>